import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Pill, Plus, AlertTriangle, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function PharmacyPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", stock: 0, unit_price: 0, category: "", barcode: "", expiry: "" });

  const load = useCallback(async () => {
    const { data } = await api.get(`/pharmacy${q ? `?q=${encodeURIComponent(q)}` : ""}`);
    setItems(data);
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    await api.post("/pharmacy", { ...form, stock: parseInt(form.stock) || 0, unit_price: parseFloat(form.unit_price) || 0 });
    toast.success("Item added");
    setOpen(false); setForm({ name: "", stock: 0, unit_price: 0, category: "", barcode: "", expiry: "" });
    load();
  };

  const canEdit = ["pharmacist", "admin", "super_admin"].includes(user.role);

  return (
    <div className="space-y-5 fade-up" data-testid="pharmacy-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h1 className="text-3xl font-bold tracking-tight">Pharmacy</h1>
          <p className="text-sm text-muted-foreground mt-1">{items.length} medicines in stock</p></div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search medicines…" className="pl-9 w-64" />
          </div>
          {canEdit && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button className="gap-2" data-testid="new-med-btn"><Plus className="size-4" /> Add medicine</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add medicine</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                  <div className="space-y-1.5"><Label>Name *</Label>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label>Stock</Label>
                      <Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>Unit price ($)</Label>
                      <Input type="number" step="0.01" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} /></div>
                  </div>
                  <div className="space-y-1.5"><Label>Category</Label>
                    <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Barcode</Label>
                    <Input value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Expiry</Label>
                    <Input type="date" value={form.expiry} onChange={(e) => setForm({ ...form, expiry: e.target.value })} /></div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button type="submit">Add</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="py-3 px-5">Medicine</th><th>Category</th><th>Stock</th><th>Price</th><th>Expiry</th>
            </tr></thead>
            <tbody>
              {items.map((m) => (
                <tr key={m.id} className="border-b border-border last:border-0 hover:bg-muted/40">
                  <td className="py-3 px-5">
                    <div className="flex items-center gap-3">
                      <Pill className="size-4 text-primary" /><span className="font-medium">{m.name}</span>
                    </div>
                  </td>
                  <td><Badge variant="outline" className="text-[10px]">{m.category || "—"}</Badge></td>
                  <td className="mono">
                    {m.stock} {m.stock < 50 && <AlertTriangle className="size-3 inline text-destructive ml-1" />}
                  </td>
                  <td className="mono">${m.unit_price?.toFixed(2)}</td>
                  <td className="mono text-xs text-muted-foreground">{m.expiry || "—"}</td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan="5" className="py-10 text-center text-muted-foreground">No medicines yet.</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
